/**
 * Care Service
 *
 * Care/Wellbeing API methods.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
import type { CoachingAction } from '@tonnie/heroes';
import type { CareStreakMilestone } from '@tonnie/types';
import type { CareSupportRequestSeverity, CreateCareSupportRequestInput } from '@tonnie/schemas';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface CarePillar {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  score: number | null;
  companyScore?: number | null;
  trend: 'up' | 'down' | 'stable' | null;
  lastUpdated: string | null;
  householdAware?: boolean;
  objectives?: string[];
}

export interface PillarDefinition {
  id: string;
  nameKey: string;
  descriptionKey: string;
  icon: string;
  color: string;
  householdAware: boolean;
  conditional: boolean;
  conditionType?: string | null;
  talentVisible: boolean;
  businessVisible: boolean;
  objectives: string[];
  questions: Array<{
    id: string;
    textKey: string;
    householdPhraseKey?: string | null;
    scale: { min: number; max: number };
  }>;
}

/** Where a resolved pillar came from in the cascade (mirrors @tonnie/heroes). */
export type TalentPillarSource = 'required' | 'company' | 'personal';

export interface ResolvedTalentPillar {
  pillarId: string;
  source: TalentPillarSource;
  /** A stated concern maps to this pillar → the FE highlights it. */
  emphasized: boolean;
}

/** Per-pillar question snapshot for a checkpoint (mirrors @tonnie/heroes). */
export interface CheckpointQuestionSet {
  checkpointType: string;
  /** Stage framing i18n key, or null when the stage has no overlay. */
  framing: string | null;
  pillars: Array<{
    pillarId: string;
    source: TalentPillarSource;
    emphasized: boolean;
    nameKey?: string;
    householdAware: boolean;
    questions: Array<{
      id: string;
      textKey: string;
      householdPhraseKey?: string | null;
      scale: { min: number; max: number };
    }>;
  }>;
}

export interface CarePillarsResponse {
  talentId: string;
  lastCheckpoint: string | null;
  pillars: Record<
    string,
    {
      score: number | null;
      companyScore: number | null;
      trend: string | null;
      lastUpdated: string | null;
    }
  >;
  pillarDefinitions: PillarDefinition[] | null;
  /** Personalized pillar set: source + concern emphasis, in display order. */
  resolvedPillars?: ResolvedTalentPillar[];
  /** Pillar codes the company mandates (the talent cannot hide). */
  requiredPillars?: string[];
  /** The talent's own selected/added pillars from their assessment. */
  focusAreas?: string[];
  /** Stated concern codes from the assessment (drive emphasis). */
  concerns?: string[];
  /**
   * The average pillar score graded by the one Care pillar-health rule — the
   * company's read of the same scores says the same. `unknown` with no score.
   */
  overallHealth: 'healthy' | 'moderate' | 'concern' | 'unknown';
  alerts: string[];
}

export interface CareCheckpoint {
  id: string;
  type: string;
  name: string;
  description?: string;
  scheduledFor: string;
  completedAt?: string | null;
  status: 'scheduled' | 'pending' | 'due' | 'overdue' | 'in_progress' | 'completed' | 'skipped';
  pillarScores?: Record<string, number> | null;
  overallScore?: number | null;
  milesAwarded?: number;
  milesReward?: number;
  /** Snapshot of the resolved per-pillar question set (null → fall back to live pillars). */
  questionSet?: CheckpointQuestionSet | null;
}

export interface CareCheckin {
  id: string;
  type: 'daily_mood' | 'quick_checkin';
  moodScore: number | null;
  responses?: Record<string, unknown>;
  source?: string;
  milesAwarded?: number;
  createdAt: string;
}

export interface CareCheckinsResponse {
  data: CareCheckin[];
  stats: {
    averageMood: number;
    totalCheckins: number;
    currentStreak: number;
  };
}

/** Resolved Care strategy (Country←Company←Program←Cohort) for a talent. */
export interface CareStrategy {
  /** Pillar codes the COMPANY has activated for this talent's scope. */
  activePillars: string[];
  /** Subset of `activePillars` the talent cannot hide (company-mandated). */
  requiredPillars: string[];
  checkpointSchedule: {
    preMove: Array<{ type: string; milesReward: number }>;
    postArrival: Array<{ type: string; daysAfterArrival: number; milesReward: number }>;
  };
  feedbackCadence: string;
  interventionThresholds: { commitmentAtRisk: number; pillarConcern: number };
}

/**
 * The citizen's own wellbeing summary. It carries no Commitment Index — that
 * band, risk level and its components are a business-only metric the company
 * reads through its own surfaces; the API leaves them out of the citizen's read.
 */
export interface WellbeingSummary {
  /**
   * Wellbeing score (0-100): 50% mood + 50% settlement, or the one that exists.
   * `null` with neither — no data is not a 0% wellbeing. Evidence-based, talent-facing.
   */
  thermometerScore: number | null;
  /** 14-day mood trend */
  moodTrend: 'up' | 'down' | 'stable';
  /** 14-day average mood (1-5); `null` with no check-in in the window. */
  averageMood: number | null;
  /** Average pillar score from the latest checkpoint (1-5); `null` before one is scored. */
  settlementScore: number | null;
  /** Settlement trend: compares last 2 checkpoints */
  settlementTrend: 'up' | 'down' | 'stable';
  /** Consecutive daily check-in days */
  streakDays: number;
  phase: 'pre_move' | 'settling_in' | 'adapting' | 'thriving';
  assessmentBaseline: {
    moodScore: number | null;
    confidenceScore: number | null;
    concerns: string[];
  } | null;
}

/** A single "way you've been cared for" gesture on the citizen momentum stream. */
export interface CareMomentumEvent {
  id: string;
  type: string;
  /** i18n key under `care.momentum.event.*`; FE translates with `values`. */
  titleKey: string;
  occurredAt: string;
  milesEarned: number;
  isHighlight: boolean;
  values?: Record<string, unknown>;
}

export interface CareMomentum {
  talentId: string;
  streakDays: number;
  totalGestures: number;
  totalMiles: number;
  events: CareMomentumEvent[];
}

/** One talent-visible scheduled interaction (score-free). */
export interface CareTalentInteraction {
  id: string;
  type: string;
  title: string;
  scheduledAt: string | null;
  status: string;
  locationOrLink: string | null;
  /**
   * The Adaptation coaching action it was sent for, or `null`. A coaching
   * record's `title` is a code (`coaching.<action>`): draw the row through
   * `interactionTitle()` from `@tonnie/heroes`, never `title` alone.
   */
  coachingAction: CoachingAction | null;
}

export interface CareInteractions {
  upcoming: CareTalentInteraction[];
  past: CareTalentInteraction[];
}

export interface CareSupportRequest {
  id: string;
  talentId: string;
  pillarId: string;
  severity: CareSupportRequestSeverity;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assignedTo?: string | null;
  createdAt: string;
  resolvedAt?: string | null;
}

export interface CareAssessmentResult {
  id: string;
  talentId: string;
  journeyInstanceId: string;
  moodScore: number | null;
  confidenceScore: number | null;
  supportNeedScore: number | null;
  selectedPillars: string[];
  concerns: string[];
  completedAt: string | null;
  assessmentData: Record<string, unknown> | null;
  isActive: boolean;
  retakeCount: number;
  createdAt: string;
}

export interface SubmitCheckpointInput {
  scores: Record<string, number>;
  responses?: Record<string, string>;
  feedback?: string;
}

export interface SubmitCheckpointResponse {
  checkpointId: string;
  status: 'completed';
  completedAt: string;
  milesEarned: number;
  nextCheckpoint?: {
    type: string;
    scheduledFor: string;
  } | null;
}

export interface MoodCheckinInput {
  moodScore: number;
  note?: string;
  source?: 'web' | 'app';
}

export interface MoodCheckinResponse {
  checkinId: string | null;
  moodScore: number;
  milesAwarded: number;
  currentStreak: number;
  /** The streak milestone this check-in reached — the server's to say; null on most days. */
  streakMilestone: CareStreakMilestone | null;
  todayAlreadyCheckedIn: boolean;
}

export interface QuickCheckinInput {
  moodScore: number;
  responses: Record<string, string>;
  pillarId?: string;
  source?: 'web' | 'app';
}

export interface QuickCheckinResponse {
  checkinId: string;
  type: 'quick_checkin';
  milesAwarded: number;
  createdAt: string;
}

/** The request a citizen raises — the shared contract (@tonnie/schemas · CARE-SUP-01). */
export type CreateSupportRequestInput = CreateCareSupportRequestInput;

export interface UpdateSupportRequestInput {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  assignedTo?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// OBJECTIVES-BASED FEEDBACK (BL-125)
//
// Mirrors the wire shapes of the platform-api Care objective-feedback read
// models. Timestamps are ISO strings over the wire (DB `Date` → JSON).
// ═══════════════════════════════════════════════════════════════════════

/** A materialized objective the talent is tracked + given feedback against. */
export interface CareObjective {
  id: string;
  tenantId: string;
  talentId: string;
  journeyInstanceId: string;
  /** Catalog objective id from the company Objectives Catalog. */
  catalogObjectiveId: string;
  /** Snapshot of the objective text at materialization time. */
  title: string;
  category: string | null;
  source: 'company' | 'personal';
  status: 'active' | 'archived';
  // ── Measurable model (migration 0080) ──
  /** number | scale | percent | milestone | rating (legacy default). */
  measureType: 'number' | 'scale' | 'percent' | 'milestone' | 'rating';
  measureUnit: string | null;
  measureDirection: 'increase' | 'decrease' | 'reach';
  measureMin: number | null;
  measureMax: number | null;
  /** Ordinal labels for `scale` (index 0..n-1). */
  scaleLabels: string[] | null;
  /** Per-talent (hybrid) baseline + target. */
  baselineValue: number | null;
  targetValue: number | null;
  createdAt: string;
  updatedAt: string;
}

/** One cadence interval — the unit feedback is given/self-evaluated against. */
export interface CareFeedbackCycle {
  id: string;
  tenantId: string;
  talentId: string;
  journeyInstanceId: string;
  cadence: string;
  periodStart: string;
  dueAt: string;
  status: 'open' | 'provider_submitted' | 'completed' | 'overdue';
  createdAt: string;
  updatedAt: string;
}

/** A feedback entry (provider OR talent self) for one objective in one cycle. */
export interface CareObjectiveFeedbackEntry {
  id: string;
  tenantId: string;
  cycleId: string;
  objectiveId: string;
  authorKind: 'provider' | 'self';
  authorUserId: string | null;
  /** Measured value this cycle (count / scale index / percent / milestone 0|1). */
  value: number | null;
  rating: number | null;
  comment: string | null;
  /**
   * BL-122 — `comment` came back `null` because the company's "Detailed
   * Feedback" visibility does not include the caller's role, NOT because the
   * author left it blank. Present (true or false) on every entry of the
   * BUSINESS read model; absent on the citizen one, where nothing is withheld
   * from a talent about their own objectives.
   *
   * Never infer "no comment" from `comment === null` alone on a business
   * surface: render the withheld state instead, or the UI tells the viewer
   * something about the talent that is not true.
   */
  commentWithheld?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Citizen read model: the talent's active objectives, their recent feedback
 * cycles, and every entry across those cycles (provider feedback + the talent's
 * own self-evaluation).
 */
export interface CareObjectiveCycleView {
  objectives: CareObjective[];
  cycles: CareFeedbackCycle[];
  feedback: CareObjectiveFeedbackEntry[];
}

/** One objective's entry in a self-eval / provider-feedback submission. */
export interface CareObjectiveFeedbackInput {
  objectiveId: string;
  /** Measured value this cycle (count / scale index / percent / milestone 0|1). */
  value?: number;
  rating?: number;
  comment?: string;
}

export interface SubmitObjectiveFeedbackInput {
  entries: CareObjectiveFeedbackInput[];
}

/**
 * Response to a talent self-evaluation: the (possibly completed) cycle plus the
 * Journey Miles awarded this submission (BL-126). `milesAwarded` is 0 on a
 * re-submit — Miles are granted only on the first self-eval for a cycle.
 */
export interface SubmitObjectiveSelfEvalResponse {
  cycle: CareFeedbackCycle;
  milesAwarded: number;
}

/**
 * Every Care row belongs to ONE journey, and every call here names it
 * (JOURNEYS-MULTI-ROUTE-PLAN · X6 · X7): a call without one used to read every
 * journey the citizen holds, or write onto the primary one whatever was on
 * screen. (The momentum stream is the person's; its journey decides only the
 * read's gate.)
 */
function forJourney(journeyId: string): { journeyId: string } {
  return { journeyId };
}

export class CareService {
  constructor(private readonly client: TonnieClient) {}

  async getAssessment(journeyId: string): Promise<CareAssessmentResult | null> {
    return this.client.get<CareAssessmentResult | null>('/api/heroes/care/me/assessment', {
      params: forJourney(journeyId),
    });
  }

  async getPillars(talentId: string, journeyId: string): Promise<CarePillarsResponse> {
    return this.client.get<CarePillarsResponse>(API_ENDPOINTS.CARE.PILLARS(talentId), {
      params: forJourney(journeyId),
    });
  }

  async getCheckpoints(talentId: string, journeyId: string): Promise<CareCheckpoint[]> {
    return this.client.get<CareCheckpoint[]>(API_ENDPOINTS.CARE.CHECKPOINTS(talentId), {
      params: forJourney(journeyId),
    });
  }

  async getCheckpoint(
    talentId: string,
    checkpointId: string,
    journeyId: string
  ): Promise<CareCheckpoint> {
    return this.client.get<CareCheckpoint>(API_ENDPOINTS.CARE.CHECKPOINT(talentId, checkpointId), {
      params: forJourney(journeyId),
    });
  }

  async submitCheckpoint(
    talentId: string,
    checkpointId: string,
    input: SubmitCheckpointInput,
    journeyId: string
  ): Promise<SubmitCheckpointResponse> {
    return this.client.post<SubmitCheckpointResponse>(
      API_ENDPOINTS.CARE.CHECKPOINT_SUBMIT(talentId, checkpointId),
      input,
      { params: forJourney(journeyId) }
    );
  }

  async submitMoodCheckin(
    talentId: string,
    input: MoodCheckinInput,
    journeyId: string
  ): Promise<MoodCheckinResponse> {
    return this.client.post<MoodCheckinResponse>(API_ENDPOINTS.CARE.CHECKIN_MOOD(talentId), input, {
      params: forJourney(journeyId),
    });
  }

  async submitQuickCheckin(
    talentId: string,
    input: QuickCheckinInput,
    journeyId: string
  ): Promise<QuickCheckinResponse> {
    return this.client.post<QuickCheckinResponse>(
      API_ENDPOINTS.CARE.CHECKIN_QUICK(talentId),
      input,
      { params: forJourney(journeyId) }
    );
  }

  async getCheckins(
    talentId: string,
    journeyId: string,
    params?: { type?: string; from?: string; to?: string }
  ): Promise<CareCheckinsResponse> {
    return this.client.get<CareCheckinsResponse>(API_ENDPOINTS.CARE.CHECKINS(talentId), {
      params: { ...params, ...forJourney(journeyId) },
    });
  }

  /** Resolved Care strategy (Country←Company←Program←Cohort) for the journey. */
  async getCareStrategy(talentId: string, journeyId: string): Promise<CareStrategy> {
    return this.client.get<CareStrategy>(API_ENDPOINTS.CARE.STRATEGY(talentId), {
      params: forJourney(journeyId),
    });
  }

  async getWellbeingSummary(talentId: string, journeyId: string): Promise<WellbeingSummary> {
    return this.client.get<WellbeingSummary>(API_ENDPOINTS.CARE.WELLBEING_SUMMARY(talentId), {
      params: forJourney(journeyId),
    });
  }

  async getMomentum(talentId: string, journeyId: string): Promise<CareMomentum> {
    return this.client.get<CareMomentum>(API_ENDPOINTS.CARE.MOMENTUM(talentId), {
      params: forJourney(journeyId),
    });
  }

  async getInteractions(talentId: string, journeyId: string): Promise<CareInteractions> {
    return this.client.get<CareInteractions>(API_ENDPOINTS.CARE.INTERACTIONS(talentId), {
      params: forJourney(journeyId),
    });
  }

  async createSupportRequest(
    talentId: string,
    input: CreateSupportRequestInput,
    journeyId: string
  ): Promise<CareSupportRequest> {
    return this.client.post<CareSupportRequest>(API_ENDPOINTS.CARE.SUPPORT(talentId), input, {
      params: forJourney(journeyId),
    });
  }

  async getSupportRequests(talentId: string, journeyId: string): Promise<CareSupportRequest[]> {
    return this.client.get<CareSupportRequest[]>(API_ENDPOINTS.CARE.SUPPORT(talentId), {
      params: forJourney(journeyId),
    });
  }

  async updateSupportRequest(
    talentId: string,
    requestId: string,
    input: UpdateSupportRequestInput,
    journeyId: string
  ): Promise<CareSupportRequest> {
    return this.client.patch<CareSupportRequest>(
      API_ENDPOINTS.CARE.SUPPORT_DETAIL(talentId, requestId),
      input,
      { params: forJourney(journeyId) }
    );
  }

  async scheduleCheckpoints(
    talentId: string,
    input: { arrivalDate: string },
    journeyId: string
  ): Promise<{ scheduled: number }> {
    return this.client.post<{ scheduled: number }>(API_ENDPOINTS.CARE.SCHEDULE(talentId), input, {
      params: forJourney(journeyId),
    });
  }

  // ─── Objectives-based feedback (BL-125) ───

  /** The journey's tracked objectives + feedback cycles + entries (X7). */
  async getObjectives(talentId: string, journeyId: string): Promise<CareObjectiveCycleView> {
    return this.client.get<CareObjectiveCycleView>(API_ENDPOINTS.CARE.OBJECTIVES(talentId), {
      params: forJourney(journeyId),
    });
  }

  /** Talent self-evaluates the journey's objectives for its open cycle. */
  async submitObjectiveSelfEval(
    talentId: string,
    input: SubmitObjectiveFeedbackInput,
    journeyId: string
  ): Promise<SubmitObjectiveSelfEvalResponse> {
    return this.client.post<SubmitObjectiveSelfEvalResponse>(
      API_ENDPOINTS.CARE.OBJECTIVE_SELF_EVAL(talentId),
      input,
      { params: forJourney(journeyId) }
    );
  }
}

export function createCareService(client: TonnieClient): CareService {
  return new CareService(client);
}
